/*
 * Copyright (c) 2022 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Wishlist from 'core/store/ducks/wishlist';
import { Rail } from 'ui/components';
import ColdStartRail from 'ui/components/Rail/ColdStartRail';
import Constants from 'configs/Constants';
import { RailBackgroundImageName } from 'types/components/ColdStart';
import translate from 'services/i18n/translate';
import { RailMetaData } from 'ui/pages/myTv/MainPage';
import RouterUtils from 'services/utils/RouterUtils';
import { PrivateRoutePath } from 'routes/paths';
import { Asset, EntryPoint } from '@wit/vtv-x-foundation-lib';
import { useHistoryWithState } from 'hooks/useHistoryWithState';
import * as BadgesStoreUtils from 'store/utils/badgesStoreUtils';
import { StoreState } from 'store/store';
import BADGES_PRIORITY_PER_SECTION_RAIL from 'configs/BadgesPriorities';
import { useRouter } from '../../../hooks/useRouter';

interface Props {
  rail: RailMetaData;
}

export default function MyTVWishlist({ rail }: Props) {
  const dispatch = useDispatch();
  const router = useRouter();

  const history = useHistoryWithState();

  const onHeaderClick = () => {
    history.push(RouterUtils.getRouteWithDynamicValues(PrivateRoutePath.MYTV_RAIL, [rail.item.id]));
  };

  const onCardClick = (asset: Asset, menuItemId?: number) => {
    router.push({ asset, menuItemId });
  };

  useEffect(() => {
    dispatch(Wishlist.ActionCreators.getItems());
  }, [dispatch]);

  const isProcessed = useSelector(Wishlist.Selectors.isGetItemsProcessed);
  const wishListAssets = useSelector((store: StoreState) =>
    BadgesStoreUtils.Selector.getAssetsWithBadges(
      store,
      BADGES_PRIORITY_PER_SECTION_RAIL.MY_TV.WISHLIST_RAIL,
      Wishlist.Selectors.getAssets,
      EntryPoint.WISHLIST
    )
  );

  if (!isProcessed) {
    return <></>;
  }

  return isProcessed && wishListAssets.length === 0 ? (
    <ColdStartRail
      railId={rail.item.id}
      railName={translate['firstTimeUse.wishlist.label']}
      railBackgroundImageName={RailBackgroundImageName.WISHLIST}
      coldStartTitle={translate['firstTimeUse.wishlist.title']}
      railDescription={translate['firstTimeUse.wishlist.subtitle']}
      className="no-background"
    />
  ) : (
    <Rail
      railId={rail.item.id}
      menuItemId={rail.item.id}
      className="no-background"
      railName={translate['firstTimeUse.wishlist.label']}
      channelId={rail.item.channelId}
      assets={wishListAssets}
      hasMore={wishListAssets.length > Constants.MAX_ASSETS_PER_RAIL}
      onHeaderClick={onHeaderClick}
      onCardClick={onCardClick}
    />
  );
}
